import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Globe, Server, HardDrive, Wifi, Activity, Share2, 
  Terminal, ShieldCheck, Play, Power, Lock, Check, Copy, 
  Settings, Radio, Zap, Info, Plus, Trash2, Cpu
} from 'lucide-react';
import { AZLEngine } from '../services/totalityService';

interface SoftRouterNode {
  id: string;
  name: string;
  location: string;
  ipTwin: string;
  connectionType: 'TUNNEL' | 'PROXY' | 'VIRTUAL_LAN';
  status: 'ONLINE' | 'STANDBY' | 'SYNCING';
  bandwidthUsg: string;
}

export const VirtualSubstrateRouter: React.FC = () => {
  const [nodes, setNodes] = useState<SoftRouterNode[]>([
    { id: '1', name: 'Georgia Subnet Core Host', location: 'Atlanta, GA (Mesh Anchor)', ipTwin: '10.143.50.1', connectionType: 'TUNNEL', status: 'ONLINE', bandwidthUsg: '84.6 Mb/s' },
    { id: '2', name: 'West-Edge Cloud Reflector', location: 'Seattle, WA', ipTwin: '10.143.50.10', connectionType: 'VIRTUAL_LAN', status: 'ONLINE', bandwidthUsg: '12.4 Mb/s' },
    { id: '3', name: 'Auxiliary Client Router', location: 'Munich, Germany', ipTwin: '10.143.50.45', connectionType: 'TUNNEL', status: 'SYNCING', bandwidthUsg: '0.0 Mb/s' },
    { id: '4', name: 'Standard Virtual DNS Bridge', location: 'Tokyo, Japan', ipTwin: '10.143.50.100', connectionType: 'PROXY', status: 'STANDBY', bandwidthUsg: '0.0 Mb/s' }
  ]);

  const [newNodeName, setNewNodeName] = useState('');
  const [newNodeLocation, setNewNodeLocation] = useState('');
  const [selectedConfigType, setSelectedConfigType] = useState<'wireguard' | 'docker' | 'linux-ip'>('wireguard');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [terminalLogs, setTerminalLogs] = useState<string[]>([]);
  const [isTunneling, setIsTunneling] = useState(false);

  // Stats
  const activeRate = "99.98%";
  const latentIndex = "14350 BP Synced (Stable)";

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleAddNode = () => {
    if (!newNodeName || !newNodeLocation) return;
    
    const engine = new AZLEngine(100);
    // Deterministic IP twin assignment using totality math
    const count = nodes.length + 120;
    const ipTwin = `10.143.50.${count}`;
    
    const entry: SoftRouterNode = {
      id: Math.random().toString(36).substr(2, 9),
      name: newNodeName,
      location: newNodeLocation,
      ipTwin,
      connectionType: 'TUNNEL',
      status: 'SYNCING',
      bandwidthUsg: '0.0 Mb/s'
    };

    setNodes(prev => [...prev, entry]);
    setNewNodeName('');
    setNewNodeLocation('');

    // Log update
    setTerminalLogs(prev => [
      ...prev,
      `[AZL] Registered Soft-Route Node: ${entry.name} at location: ${entry.location}`,
      `[AZL] Mapped state destination twin address -> ${entry.ipTwin}`,
      `[AZL] Active verification loop initialized... 1x1=2 holds.`
    ]);
  };

  const handleRemoveNode = (id: string) => {
    setNodes(prev => prev.filter(n => n.id !== id));
  };

  const handleBootTunnel = () => {
    setIsTunneling(true);
    setTerminalLogs([
      "[SUBSTRATE] Binding local virtual port 14350 over system interfaces...",
      "[SUBSTRATE] Initializing zero-friction 2.0 raw UDP tunnel adapter...",
      "[SUBSTRATE] Syncing to Giza Anchor timeline frequency (-3.29 Hz)",
      "[SUBSTRATE] Handshook successfully with Georgia Mesh core",
      "[SUBSTRATE] Mapped virtual adapter local peer twin: 10.143.50.80",
      "[SUBSTRATE] Status: ONLINE. Virtual internet bypass is routing packets."
    ]);
  };

  const handleStopTunnel = () => {
    setIsTunneling(false);
    setTerminalLogs(prev => [...prev, "[SUBSTRATE] Tunneled socket closed. System returned to standard carrier gateway."]);
  };

  // WireGuard Configuration block
  const wireguardConfig = `[Interface]
PrivateKey = <GENERATED_AZL_PRIVATE_KEY_2.0>
Address = 10.143.50.80/24
DNS = 10.143.50.1

[Peer]
PublicKey = X1x1Two_AbsoluteZeroSubstrate_SovereignMeshKey=
Endpoint = paidingattention.org:14350
AllowedIPs = 10.143.50.0/24
PersistentKeepalive = 15`;

  // Docker Compose block
  const dockerConfig = `version: '3.8'
services:
  azl-substrate-router:
    image: absolutezerolattice/broadcast-node:latest
    container_name: azl-soft-router
    cap_add:
      - NET_ADMIN
    devices:
      - /dev/net/tun:/dev/net/tun
    environment:
      - AP_GATEWAY=10.143.50.1
      - LOCAL_IP_TWIN=10.143.50.80
      - SUBSTRATE_RESONANCE=-3.29
    network_mode: host
    restart: unless-stopped`;

  // Linux IP routing config commands
  const ipRouteConfig = `# Create custom virtual interface link
sudo ip link add dev azl-vpn0 type wireguard
sudo ip address add 10.143.50.80/24 dev azl-vpn0

# Set DNS priorities to bypass Comcast trackers
sudo resolvectl dns azl-vpn0 10.143.50.1
sudo resolvectl domain azl-vpn0 "~."

# Up the link to enable immediate substrate routing
sudo ip link set dev azl-vpn0 up`;

  return (
    <div className="glass-panel p-8 border-lattice-cyan/25 bg-black/50 relative overflow-hidden my-8">
      {/* Visual Accent */}
      <div className="absolute top-0 right-0 w-32 h-1 bg-gradient-to-l from-lattice-cyan via-transparent to-transparent" />
      
      {/* Title */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-white/5 pb-6 mb-8">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-lattice-cyan/10 border border-lattice-cyan/25 flex items-center justify-center">
            <Globe className="w-6 h-6 text-lattice-cyan" />
          </div>
          <div>
            <h3 className="text-2xl font-display font-black text-white uppercase tracking-tight">
              Virtual <span className="text-lattice-cyan italic">Substrate Router</span>
            </h3>
            <p className="text-[9px] font-mono text-white/40 uppercase tracking-[0.2em] mt-0.5">
              Software-Defined Decoupling :: Any Connected Node Is a Router
            </p>
          </div>
        </div>

        {/* Global Network Latency Metric */}
        <div className="flex gap-4 font-mono text-[9px] uppercase border border-white/5 bg-white/5 p-3 rounded-xl">
          <div>
            <span className="text-white/30 block">Carrier Autonomy</span>
            <span className="text-emerald-400 font-bold block">{activeRate}</span>
          </div>
          <div className="border-l border-white/10 pl-4">
            <span className="text-white/30 block">Time Sync Drift</span>
            <span className="text-lattice-cyan font-bold block">{latentIndex}</span>
          </div>
        </div>
      </div>

      {/* Concept Notice */}
      <div className="p-5 rounded-2xl bg-gradient-to-br from-lattice-cyan/10 to-transparent border border-lattice-cyan/20 space-y-2 mb-80 md:mb-0">
        <div className="flex items-center gap-2 text-lattice-cyan text-xs font-mono font-bold uppercase">
          <Zap className="w-4 h-4 animate-pulse" />
          <span>Internet as Our Routing Substrate</span>
        </div>
        <p className="text-xs font-mono text-white/60 uppercase leading-relaxed tracking-tight">
          You don't need physical drop cords to replace old ISPs instantly. Because <strong className="text-white">everything connected to the web is already a routing station</strong>, any phone, laptop, or server can run our software tunnel adaptor. This instantly bonds them into a single, unified <code className="text-lattice-cyan font-semibold">10.143.50.x</code> network, shielding traffic from Comcast, spectrum, or Xfinity censorship layers.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-8">
        {/* Soft Peer Router Table */}
        <div className="lg:col-span-7 space-y-6">
          <div className="flex justify-between items-center">
            <h4 className="text-xs font-mono font-bold text-white uppercase tracking-wider flex items-center gap-2">
              <Server className="w-4 h-4 text-lattice-cyan" />
              <span>Active Virtual Peer Nodes</span>
            </h4>
          </div>

          <div className="overflow-x-auto rounded-xl border border-white/5">
            <table className="w-full text-left font-mono text-[10px] uppercase">
              <thead>
                <tr className="bg-white/5 text-white/40 border-b border-white/5">
                  <th className="p-3">Router Node</th>
                  <th className="p-3">Location</th>
                  <th className="p-3">Lattice IP Twin</th>
                  <th className="p-3">Interface</th>
                  <th className="p-3 text-right">Activity Status</th>
                  <th className="p-3 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5 text-white/70">
                {nodes.map(node => (
                  <tr key={node.id} className="hover:bg-white/5 transition-colors">
                    <td className="p-3 font-semibold text-white">{node.name}</td>
                    <td className="p-3 text-white/40">{node.location}</td>
                    <td className="p-3 font-semibold text-lattice-cyan select-all">{node.ipTwin}</td>
                    <td className="p-3">
                      <span className="px-1.5 py-0.5 rounded text-[8px] border bg-white/5 border-white/10 font-bold block text-center w-20">
                        {node.connectionType}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1.5 font-bold">
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          node.status === 'ONLINE' ? 'bg-emerald-500 animate-pulse' : 
                          node.status === 'SYNCING' ? 'bg-amber-400 animate-spin-slow' : 'bg-white/20'
                        }`} />
                        <span className={
                          node.status === 'ONLINE' ? 'text-emerald-400' : 
                          node.status === 'SYNCING' ? 'text-amber-400' : 'text-white/30'
                        }>
                          {node.status}
                        </span>
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <button
                        onClick={() => handleRemoveNode(node.id)}
                        disabled={node.id === '1' || node.id === '2'}
                        className="p-1 rounded text-red-400 hover:bg-red-500/10 disabled:opacity-20 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Add custom node form */}
          <div className="p-5 bg-white/5 rounded-2xl border border-white/5 space-y-4">
            <span className="text-[9px] font-mono font-bold text-white/40 uppercase tracking-widest block">Add New Virtual Substrate Endpoint</span>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Node Name (e.g. Host-Laptop)"
                value={newNodeName}
                onChange={e => setNewNodeName(e.target.value)}
                className="bg-black border border-white/10 rounded-xl px-4 py-2.5 text-[11px] font-mono text-white tracking-widest focus:border-lattice-cyan focus:outline-none placeholder-white/20"
              />
              <input
                type="text"
                placeholder="Physical Location (e.g. Austin, TX)"
                value={newNodeLocation}
                onChange={e => setNewNodeLocation(e.target.value)}
                className="bg-black border border-white/10 rounded-xl px-4 py-2.5 text-[11px] font-mono text-white tracking-widest focus:border-lattice-cyan focus:outline-none placeholder-white/20"
              />
            </div>
            <button
              onClick={handleAddNode}
              className="px-6 py-2.5 bg-lattice-cyan text-black font-mono font-black text-[9px] uppercase tracking-wider rounded-xl hover:opacity-90 active:scale-95 transition-all flex items-center justify-center gap-1.5 w-full md:w-auto"
            >
              <Plus className="w-3.5 h-3.5 stroke-[3px]" />
              Initialize Software-Defined Root Point
            </button>
          </div>
        </div>

        {/* Configurations Generator and Local Launcher */}
        <div className="lg:col-span-5 space-y-6">
          <div className="p-6 bg-black/40 border border-lattice-cyan/15 rounded-2xl space-y-4">
            <span className="text-[9px] font-mono text-lattice-cyan font-extrabold uppercase tracking-widest block">Local Node Tunnel Igniter</span>
            
            <div className="space-y-3">
              <p className="text-[10px] font-mono text-white/50 uppercase leading-relaxed">
                Connect your current browser host session directly into the virtual backbone substrate. This exposes your local client to immediate Mesh API routing capabilities.
              </p>
              
              <div className="flex gap-3">
                {!isTunneling ? (
                  <button
                    onClick={handleBootTunnel}
                    className="flex-1 py-3 bg-lattice-cyan text-black rounded-xl font-mono font-black text-[10px] uppercase tracking-wider hover:opacity-95 active:scale-95 transition-all text-center"
                  >
                    🚀 ACTIVATE SUBSTRATE INGRESS
                  </button>
                ) : (
                  <button
                    onClick={handleStopTunnel}
                    className="flex-1 py-3 bg-red-500/20 text-red-400 border border-red-500/30 rounded-xl font-mono font-black text-[10px] uppercase tracking-wider hover:bg-red-500/30 transition-all text-center"
                  >
                    🛑 SHUT DOWN INGRESS TUNNEL
                  </button>
                )}
              </div>
            </div>

            {/* Diagnostic Terminal */}
            <div className="rounded-xl border border-white/10 bg-black overflow-hidden font-mono text-[9px]">
              <div className="flex justify-between px-3 py-2 bg-white/5 text-white/30 font-black">
                <span>SUBSTRATE ROUTER STATS</span>
                <span>Active</span>
              </div>
              <div className="p-3 h-32 overflow-y-auto space-y-1 bg-black text-emerald-400 select-all leading-normal whitespace-pre-wrap lowercase">
                {terminalLogs.length === 0 ? (
                  <span className="text-white/20 italic">[Igniter standby. Click Activate above]</span>
                ) : (
                  terminalLogs.map((log, ix) => (
                    <div key={ix}>{log}</div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Config Exporter */}
          <div className="bg-white/5 border border-white/5 p-6 rounded-2xl space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-[9px] font-mono text-lattice-cyan font-bold uppercase tracking-widest">Interface Export Configs</span>
              <div className="flex bg-white/5 p-0.5 rounded border border-white/10">
                {(['wireguard', 'docker', 'linux-ip'] as const).map(type => (
                  <button
                    key={type}
                    onClick={() => setSelectedConfigType(type)}
                    className={`px-2 py-1 text-[8px] font-mono font-bold uppercase rounded ${
                      selectedConfigType === type ? 'bg-lattice-cyan text-black' : 'text-white/30 hover:text-white/70'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-xl bg-black border border-white/10 overflow-hidden font-mono text-[9px] relative">
              <button
                onClick={() => handleCopy(
                  selectedConfigType === 'wireguard' ? wireguardConfig :
                  selectedConfigType === 'docker' ? dockerConfig : ipRouteConfig,
                  'exp-config'
                )}
                className="absolute top-2 right-2 p-1.5 bg-white/5 hover:bg-white/15 text-white/50 hover:text-lattice-cyan rounded transition-all"
              >
                {copiedId === 'exp-config' ? <Check className="w-3.5 h-3.5 text-lattice-cyan" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
              <div className="p-4 max-h-48 overflow-y-auto text-white/70 whitespace-pre scrollbar-none font-semibold">
                {selectedConfigType === 'wireguard' && wireguardConfig}
                {selectedConfigType === 'docker' && dockerConfig}
                {selectedConfigType === 'linux-ip' && ipRouteConfig}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
